import React, { useEffect, useState } from "react";
import DialogContent from "@mui/material/DialogContent";
import {
  IFlight,
  IGroundEvent,
  IFullAircraftConfiguration,
  IBulkOpsState,
  BulkActionType,
  IBulkEventPayload,
} from "@vmosolutions/oc-interfaces";

import { IRootState } from "src/redux/reducers/CombinedReducers";

import { bulkEvent } from "src/redux/actions/flights/FlightData.Actions";
import { LoadingMessage } from "../../LoadingMessage/LoadingMessage";
import { DialogModalStyles } from "../DialogModalWrapper";
import { useDispatch, useSelector } from "react-redux";
import { DialogActions, useTheme } from "@mui/material";
import { EventModalTypes } from "../DialogModalWrapper";
import { FlightChipCollection } from "src/components/Flights/FlightChip";
import { ModalTitle, ModalButton } from "../DialogModalComponents";
import { SelectField } from "../../FormikComponents/SelectField";
import { ModalMessage } from "../../ModalComponents/ModalComponents";
import { isFlight } from "src/redux/reducers/FlightTimelineReducer";

// API Calls
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { setSelectedEvents } from "src/redux/actions/flights/FlightTimeline.Actions";

/**
 * @param isOpen - to keep track of whither the modal should be open or closed
 * @param handleClose - sets isOpen to true or false in DialogModalWrapper.tsx
 * @param setActiveFlightModal - sets the  state of activeFlightModal in FlightTimelineWrapper.tsx
 * @returns the modal
 */

interface IConfigAircraftModalProps {
  isOpen: boolean;
  handleClose: (value: boolean) => void;
  setActiveEventModal: (EventModalTypes: EventModalTypes) => void;
}

export const ConfigAircraftModal = (props: IConfigAircraftModalProps): JSX.Element => {
  const selectedEvents: (IFlight | IGroundEvent)[] = useSelector(
    (state: IRootState) => state.timelineFlights.selectedEvents
  );
  // const structures = useSelector((state: IRootState) => state.structures.customerStructure);
  // const flightData = useSelector((state: IRootState) => state.timelineFlights.flightData);

  // For BULK batch tracking
  const [batchId, setBatchId] = useState<any>(null);
  const batchJob: IBulkOpsState = useSelector((state: IRootState) => state.bulkBatch[batchId]);
  const [building, setBuilding] = useState<boolean>(false);
  const [buildStatus, setBuildStatus] = useState<string>(null);

  const [selectedConfig, setSelectedConfig] = useState<IFullAircraftConfiguration>(null);
  const [aircraftConfigOptions, setAircraftConfigOptions] = useState<IFullAircraftConfiguration[]>([]);
  const dispatch = useDispatch();
  const theme = useTheme();
  const { isOpen, handleClose, setActiveEventModal } = props;

  const selectedFlights: IFlight[] = React.useMemo(() => {
    return selectedEvents.filter((event: IFlight | IGroundEvent) => isFlight(event));
  }, [selectedEvents]);

  useEffect(() => {
    if (batchJob) {
      //console.log(batchJob);
      const { records, successfulEventIds, failedEvents } = batchJob;
      const failedUpdates = failedEvents?.length > 0 ? `, ${failedEvents?.length} flights failed` : "";
      setBuildStatus(`${successfulEventIds?.length} of ${records} flights successfully updated${failedUpdates}`);
      // Build completed
      if (successfulEventIds?.length + failedEvents?.length === records) {
        setBuilding(false);
      }
    }
  }, [batchJob]);

  // Retrieve available configurations for selected aircraft.
  useEffect(() => {
    if (selectedFlights?.length > 0 && selectedFlights[0]?.aircraft?._id) {
      const aircraftID = selectedFlights[0].aircraft._id;
      const getConfigs = async (): Promise<void> => {
        const configs = await AmplifyAPIWrapper.Get<IFullAircraftConfiguration[]>(
          `/aircraftConfiguration?aircraftId=${aircraftID}`
        );

        if (Array.isArray(configs) && configs.length > 0) {
          setAircraftConfigOptions(configs);
        }
      };
      getConfigs();
    } else {
      setAircraftConfigOptions([]);
    }
  }, [selectedFlights]);

  //check to make sure you aren't working with an empty array
  if (selectedEvents.length <= 0) {
    // Debug.log("selectedEvents array is empty")
    return null;
  }

  //get the craft id, based on that id get the config options for that craft
  // const selectedFlightAircraftId: string = flightData[selectedFlights[0].aircraft._id].flights.find(
  //   (flight: IFlight) => {
  //     return flight._id === selectedFlights[0]._id;
  //   }
  // ).aircraft._id;

  // const selectedACStructure: IAircraft = structures.aircraft.find(
  //   (craft: IAircraft) => craft._id === selectedFlightAircraftId
  // );
  // // @ts-ignore - AIRCRAFT CONFIG FIX REQUIRED
  // const configArray: IAircraftConfiguration[] = selectedACStructure?.aircraftConfiguration;

  // //set up an array to pass to modal with the title and id of aircraft configs
  // const configOptions = configArray.map((config: any) => {
  //   return {
  //     value: config._id,
  //     label: config.title,
  //   };
  // });

  //////////////
  // HANDLERS //
  //////////////

  const sameAircraftCheck = selectedFlights.every(
    (flight: IFlight) => flight.aircraft._id === selectedFlights[0].aircraft._id
  );

  const nativeFormHandler = (event: any): void => {
    setSelectedConfig(event);
  };

  const handleCancel = (): void => {
    // If a bulk operation was started and users choose to close the modal, clear all selected events making it easier for them to start a new bulk operation.
    if (buildStatus) dispatch(setSelectedEvents([]));
    handleClose(false);
    setActiveEventModal(EventModalTypes.None);
  };

  const handleConfirm = async (): Promise<void> => {
    //select the aircraft configuration matching the configId selcted in modal
    //const aircraftConfiguration: IAircraftConfiguration = configArray.find(
    //  (config: IAircraftConfiguration) => config._id === selectedConfig.value
    //);

    // updates only the "aircraft configuration" object in side of the flight document
    if (selectedConfig) {
      setBuilding(true);
      setBuildStatus("Changing aircraft configuration for selected flights");
      const selectedFlightIds = selectedFlights.map((flight: IFlight) => flight._id);
      const bulkPayload: IBulkEventPayload = {
        actionType: BulkActionType.UPDATE_FLIGHT,
        batchIds: selectedFlightIds,
        payload: { aircraftConfiguration: selectedConfig._id, updateType: "EditFlight" },
      };
      const newBatchId = await dispatch(bulkEvent(bulkPayload, selectedFlights?.length));
      setBatchId(newBatchId);
    }
    //set the modal type to none on close
    //setActiveEventModal(EventModalTypes.None);
  };

  //////////////
  //  Return  //
  //////////////

  if (isOpen && sameAircraftCheck) {
    return (
      <DialogContent sx={DialogModalStyles(theme)}>
        <div className="modal_content">
          <FlightChipCollection flights={selectedFlights} disableContext={true} disableFocus={true} />
          <ModalTitle title="Aircraft Configuration" />

          <SelectField
            name="acConfig"
            className="modal_selection"
            label="AC Config"
            options={aircraftConfigOptions}
            getOptionLabel={(option: any) => option.title}
            getOptionValue={(option: any) => option._id}
            onChange={nativeFormHandler}
            value={selectedConfig}
            menuPosition="fixed"
            isSearchable={true}
            openMenuOnFocus={true}
            isClearable={true}
            placeholder="select Aircraft Config"
            maxMenuHeight={400}
          />

          <DialogActions>
            <ModalButton label={buildStatus ? "Close" : "Cancel"} handler={handleCancel}></ModalButton>
            <ModalButton label="Confirm" disable={building || !!buildStatus} handler={handleConfirm}></ModalButton>
          </DialogActions>

          <div className="modal_status">
            {!!buildStatus && <LoadingMessage message={buildStatus} animate={building} />}
          </div>
        </div>
      </DialogContent>
    );
  } else if (isOpen && !sameAircraftCheck) {
    return (
      <DialogContent sx={DialogModalStyles(theme)}>
        <div className="modal_content">
          <FlightChipCollection flights={selectedFlights} />

          <ModalTitle title="Config Aircraft"></ModalTitle>

          <ModalMessage message="Flights must be all on the same aircraft"></ModalMessage>

          <DialogActions>
            <ModalButton label="Back" handler={handleCancel}></ModalButton>
          </DialogActions>
        </div>
      </DialogContent>
    );
  } else {
    return null;
  }
};
